import { getRequiredChartCount, LAYOUT_CONFIGS } from './chartLayoutConfig';
import { barsbackForInterval } from '../components/shared/ChartIntervalControl';
import { resolveTradableSymbol } from './futuresContracts';

const LAYOUT_VALUES = new Set(LAYOUT_CONFIGS.map((layout) => layout.value));
const VALID_UNITS = new Set(['Minute', 'Daily', 'Weekly', 'Monthly']);
const MAX_NAME_LENGTH = 40;

export function shortIntervalLabel(interval, unit) {
  const value = Number(interval);
  if (unit === 'Daily') return `${value}D`;
  if (unit === 'Weekly') return `${value}W`;
  if (unit === 'Monthly') return `${value}M`;
  if (unit === 'Minute' && value >= 60 && value % 60 === 0) return `${value / 60}h`;
  return `${value}m`;
}

function normalizeSlot(slot) {
  if (!slot || typeof slot !== 'object') return null;
  const unit = VALID_UNITS.has(slot.unit) ? slot.unit : 'Minute';
  const interval = Number(slot.interval);
  const rawTicker = slot.ticker == null ? '' : String(slot.ticker).trim().toUpperCase();
  return {
    ticker: rawTicker || null,
    interval: Number.isFinite(interval) && interval > 0 ? interval : 5,
    unit,
  };
}

/** Visible charts for the current arrangement: count, ticker, interval, and unit. */
export function snapshotVisibleCharts(charts, chartLayout) {
  const count = getRequiredChartCount(chartLayout) || 1;
  const slots = [];
  for (let i = 0; i < count; i += 1) {
    const chart = charts?.[i];
    const slot = normalizeSlot(chart) || { ticker: null, interval: 5, unit: 'Minute' };
    slots.push(slot);
  }
  return { chartLayout, charts: slots };
}

export function normalizeSavedChartLayouts(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const layouts = [];
  raw.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const id = typeof item.id === 'string' ? item.id.trim() : '';
    const name = typeof item.name === 'string' ? item.name.trim() : '';
    if (!id || !name || !LAYOUT_VALUES.has(item.chartLayout) || seen.has(id)) return;
    const charts = (Array.isArray(item.charts) ? item.charts : [])
      .map(normalizeSlot)
      .filter(Boolean)
      .slice(0, getRequiredChartCount(item.chartLayout));
    if (charts.length === 0) return;
    seen.add(id);
    layouts.push({
      id,
      name: name.slice(0, MAX_NAME_LENGTH),
      chartLayout: item.chartLayout,
      charts,
    });
  });
  return layouts;
}

export function nextLayoutName(layouts) {
  const names = new Set((layouts || []).map((layout) => (layout?.name || '').trim().toLowerCase()));
  let n = (layouts || []).length + 1;
  let name = `Layout ${n}`;
  while (names.has(name.toLowerCase())) {
    n += 1;
    name = `Layout ${n}`;
  }
  return name;
}

export function formatLayoutSummary(layout) {
  const charts = layout?.charts || [];
  const count = charts.length || getRequiredChartCount(layout?.chartLayout) || 1;
  const head = `${count} chart${count === 1 ? '' : 's'}`;
  if (charts.length === 0) return head;
  const parts = charts.map((chart) => {
    const ticker = chart.ticker || '—';
    return `${ticker} ${shortIntervalLabel(chart.interval, chart.unit)}`;
  });
  return `${head} · ${parts.join(', ')}`;
}

export function savedLayoutMatchesWorkspace(saved, charts, chartLayout) {
  if (!saved || saved.chartLayout !== chartLayout) return false;
  const snap = snapshotVisibleCharts(charts, chartLayout);
  const savedCharts = saved.charts || [];
  if (snap.charts.length !== savedCharts.length) return false;
  return snap.charts.every((slot, index) => {
    const other = savedCharts[index];
    return slot.ticker === (other?.ticker || null)
      && slot.interval === other?.interval
      && slot.unit === other?.unit;
  });
}

export function layoutHasMixedTickers(charts) {
  const tickers = (charts || []).map((chart) => chart?.ticker).filter(Boolean);
  return new Set(tickers).size > 1;
}

export function layoutHasMixedTimeframes(charts) {
  const keys = (charts || []).map((chart) => `${chart?.interval}|${chart?.unit}`);
  return new Set(keys).size > 1;
}

function tradableTicker(ticker) {
  if (!ticker) return null;
  return resolveTradableSymbol(ticker) || ticker;
}

/**
 * Apply a saved layout onto the chart list.
 * Keeps chart ids and other fields; updates ticker, interval, and unit for visible slots.
 * Adds charts when the saved arrangement needs more than currently exist.
 */
export function applySavedLayoutToCharts(existingCharts, saved, now = () => Date.now()) {
  if (!saved || !LAYOUT_VALUES.has(saved.chartLayout)) return null;
  const needed = getRequiredChartCount(saved.chartLayout) || 1;
  const slots = (Array.isArray(saved.charts) ? saved.charts : [])
    .map(normalizeSlot)
    .filter(Boolean)
    .slice(0, needed);
  const next = (existingCharts || []).map((chart) => ({ ...chart }));

  while (next.length < needed) {
    const index = next.length;
    const slot = slots[index] || { ticker: null, interval: 5, unit: 'Minute' };
    const ticker = tradableTicker(slot.ticker) || 'SPY';
    next.push({
      id: `chart${now()}_${index}`,
      ticker,
      interval: slot.interval,
      unit: slot.unit,
      barsback: barsbackForInterval(slot.interval, slot.unit),
      indicators: [],
    });
  }

  for (let i = 0; i < needed; i += 1) {
    const slot = slots[i];
    if (!slot) continue;
    const ticker = tradableTicker(slot.ticker) || next[i].ticker;
    const intervalChanged = next[i].interval !== slot.interval || next[i].unit !== slot.unit;
    next[i] = {
      ...next[i],
      ticker,
      interval: slot.interval,
      unit: slot.unit,
      barsback: intervalChanged
        ? barsbackForInterval(slot.interval, slot.unit)
        : (next[i].barsback || barsbackForInterval(slot.interval, slot.unit)),
    };
  }

  return {
    charts: next,
    chartLayout: saved.chartLayout,
    visibleIds: next.slice(0, needed).map((chart) => chart.id),
  };
}
